/*
 * useWizardDraft — shared draft persistence for multi-step creation wizards.
 *
 * WHY THIS EXISTS
 * ---------------
 * The AutoLet and ExteLet wizards reset their form on every open and keep
 * progress only in React state, so closing the slideout, navigating away to
 * fetch a detail from elsewhere in the platform, or a mid-way failure forced
 * the operator to start over from step 1. This hook persists the in-progress
 * form + step so the wizard can restore it on reopen, with an explicit
 * "Start fresh" escape hatch and a "Save & continue later" action.
 *
 * TWO-TIER PERSISTENCE (V331)
 * ---------------------------
 *   • localStorage — synchronous, instant, offline-safe; the primary read on
 *     reopen (same device, same session).
 *   • server draft API (/api/v1/agent-drafts) — durable + cross-device, and the
 *     source for the central "Drafts" list on the inventory page. Enabled by
 *     passing `server:{archetype}`. Writes are debounced so per-keystroke saves
 *     don't spam the API; the created draft id is tracked so saves upsert (never
 *     duplicate) and clear/delete removes it. Best-effort — a server hiccup never
 *     costs the operator their in-progress form (localStorage still holds it).
 *
 * SECURITY — secret fields (API keys, tokens) must NEVER be persisted. Pass them
 * in `excludeFields`; they are stripped before BOTH the localStorage and server
 * writes, and re-entered by the operator on resume.
 *
 * Pass STABLE `excludeFields` / `server` references (module-level or memoized) so
 * the memoized callbacks don't churn every render.
 */
import { useCallback, useRef } from 'react';
import * as api from '../services/api';

/* Stable default so an omitted excludeFields doesn't create a fresh [] each render
 * (which would make the memoized callbacks — and any effect that depends on them —
 * churn every render). Callers that DO pass excludeFields should pass a stable ref. */
const EMPTY_EXCLUDES = [];

export function useWizardDraft(storageKey, { excludeFields = EMPTY_EXCLUDES, server = null } = {}) {
  // Tracks the server-side draft row id so repeated saves upsert instead of
  // creating duplicates, and clear() can delete the right row.
  const serverIdRef = useRef(null);
  const debounceRef = useRef(null);

  const strip = useCallback((snapshot) => {
    const out = { ...snapshot };
    if (out.form && typeof out.form === 'object') {
      out.form = { ...out.form };
      for (const f of excludeFields) delete out.form[f];
    }
    return out;
  }, [excludeFields]);

  const save = useCallback((snapshot) => {
    if (!snapshot) return;
    const out = strip(snapshot);
    // Tier 1 — localStorage (instant, offline-safe). Carry the server id forward
    // so a same-device resume keeps upserting the same server row.
    if (storageKey) {
      try {
        localStorage.setItem(storageKey, JSON.stringify({
          ...out, __serverId: serverIdRef.current || undefined, __savedAt: Date.now(),
        }));
      } catch {
        /* storage unavailable / quota exceeded — non-fatal, just no local draft */
      }
    }
    // Tier 2 — server write-through (debounced), when a server archetype is set.
    if (server?.archetype && typeof api.saveAgentDraft === 'function') {
      if (debounceRef.current) clearTimeout(debounceRef.current);
      debounceRef.current = setTimeout(async () => {
        try {
          const res = await api.saveAgentDraft({
            id: serverIdRef.current || undefined,
            archetype: server.archetype,
            name: typeof server.name === 'function' ? server.name(out) : (out?.form?.name || null),
            form: out,
          });
          if (res?.ok && res.data?.id) serverIdRef.current = res.data.id;
        } catch {
          /* non-fatal — localStorage still holds the draft */
        }
      }, 800);
    }
  }, [storageKey, server, strip]);

  const load = useCallback(() => {
    if (!storageKey) return null;
    try {
      const raw = localStorage.getItem(storageKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      // Re-adopt the server id so the next save upserts the same row (no dupes).
      if (parsed && parsed.__serverId) serverIdRef.current = parsed.__serverId;
      return parsed;
    } catch {
      return null;
    }
  }, [storageKey]);

  const clear = useCallback(() => {
    if (storageKey) {
      try { localStorage.removeItem(storageKey); } catch { /* noop */ }
    }
    if (debounceRef.current) { clearTimeout(debounceRef.current); debounceRef.current = null; }
    if (server?.archetype && serverIdRef.current && typeof api.deleteAgentDraft === 'function') {
      const id = serverIdRef.current;
      serverIdRef.current = null;
      api.deleteAgentDraft(id).catch(() => { /* best-effort */ });
    }
  }, [storageKey, server]);

  const exists = useCallback(() => {
    if (!storageKey) return false;
    try { return localStorage.getItem(storageKey) != null; } catch { return false; }
  }, [storageKey]);

  /* Adopt an existing server draft id — used when resuming a draft picked from the
   * central Drafts panel, so subsequent auto-saves update that row rather than
   * forking a new one. */
  const adoptServerDraft = useCallback((id) => { serverIdRef.current = id || null; }, []);

  return { save, load, clear, exists, adoptServerDraft };
}

export default useWizardDraft;
